import React, { useState, useRef, useEffect } from 'react';
import './GreekInput.css';

/**
 * Greek character mappings for virtual keyboard
 * Maps common Latin characters to their Greek equivalents
 */
const GREEK_CHAR_MAP: Record<string, string> = {
  'a': 'α', 'b': 'β', 'g': 'γ', 'd': 'δ', 'e': 'ε', 'z': 'ζ',
  'h': 'η', 'q': 'θ', 'i': 'ι', 'k': 'κ', 'l': 'λ', 'm': 'μ',
  'n': 'ν', 'x': 'ξ', 'o': 'ο', 'p': 'π', 'r': 'ρ', 's': 'σ',
  't': 'τ', 'u': 'υ', 'f': 'φ', 'c': 'χ', 'y': 'ψ', 'w': 'ω',
  'A': 'Α', 'B': 'Β', 'G': 'Γ', 'D': 'Δ', 'E': 'Ε', 'Z': 'Ζ',
  'H': 'Η', 'Q': 'Θ', 'I': 'Ι', 'K': 'Κ', 'L': 'Λ', 'M': 'Μ',
  'N': 'Ν', 'X': 'Ξ', 'O': 'Ο', 'P': 'Π', 'R': 'Ρ', 'S': 'Σ',
  'T': 'Τ', 'U': 'Υ', 'F': 'Φ', 'C': 'Χ', 'Y': 'Ψ', 'W': 'Ω'
};

/**
 * Common Greek characters for virtual keyboard
 */
const GREEK_KEYBOARD_LAYOUT = [
  ['α', 'β', 'γ', 'δ', 'ε', 'ζ', 'η', 'θ', 'ι', 'κ'],
  ['λ', 'μ', 'ν', 'ξ', 'ο', 'π', 'ρ', 'σ', 'τ', 'υ'],
  ['φ', 'χ', 'ψ', 'ω', 'ς', 'ά', 'έ', 'ή', 'ί', 'ό'],
  ['ύ', 'ώ', 'ϊ', 'ϋ', 'ΐ', 'ΰ', ';', '·', "'", "'"]
];

interface GreekInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  multiline?: boolean;
  rows?: number;
  showVirtualKeyboard?: boolean;
  showInputHints?: boolean;
  className?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
}

/**
 * GreekInput component for Greek text entry
 * Supports Greek Unicode characters with virtual keyboard and input method hints
 * Requirements: 8.1, 8.3, 8.4
 */
const GreekInput: React.FC<GreekInputProps> = ({
  value,
  onChange,
  placeholder = 'Enter Greek text...',
  label,
  multiline = false,
  rows = 3,
  showVirtualKeyboard = true,
  showInputHints = true,
  className = '',
  id,
  required = false,
  disabled = false
}) => {
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  /**
   * Handle text input change
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Update cursor position
    setCursorPosition(e.target.selectionStart || 0);
  };

  /**
   * Insert Greek character at cursor position
   */
  const insertGreekChar = (char: string) => {
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = value.substring(0, start) + char + value.substring(end);
    
    onChange(newValue);
    
    // Set cursor position after inserted character
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + char.length, start + char.length);
    }, 0);
  };

  /**
   * Toggle virtual keyboard visibility
   */
  const toggleKeyboard = () => {
    setShowKeyboard(!showKeyboard);
  };

  /**
   * Validate that text contains Greek characters
   */
  const hasGreekCharacters = (text: string): boolean => {
    // Greek Unicode range: U+0370 to U+03FF
    const greekRegex = /[\u0370-\u03FF]/;
    return greekRegex.test(text);
  };

  /**
   * Check if current value renders correctly
   */
  const isValidGreekText = (): boolean => {
    if (!value) return true;
    // Check if text contains valid Unicode characters
    try {
      // Attempt to encode and decode to verify valid Unicode
      const encoded = encodeURIComponent(value);
      const decoded = decodeURIComponent(encoded);
      return decoded === value;
    } catch {
      return false;
    }
  };

  const inputId = id || `greek-input-${Math.random().toString(36).substr(2, 9)}`;
  const hasGreek = hasGreekCharacters(value);
  const isValid = isValidGreekText();

  return (
    <div className={`greek-input-container ${className}`}>
      {label && (
        <label htmlFor={inputId} className="greek-input-label">
          {label}
          {required && <span className="required-indicator">*</span>}
        </label>
      )}

      <div className="greek-input-wrapper">
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            id={inputId}
            className={`greek-input greek-textarea ${!isValid ? 'invalid' : ''}`}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            rows={rows}
            required={required}
            disabled={disabled}
            onFocus={() => showInputHints && setShowHints(true)}
            onBlur={() => setShowHints(false)}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            id={inputId}
            className={`greek-input ${!isValid ? 'invalid' : ''}`}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            onFocus={() => showInputHints && setShowHints(true)}
            onBlur={() => setShowHints(false)}
          />
        )}

        {showVirtualKeyboard && (
          <button
            type="button"
            className="keyboard-toggle-btn"
            onClick={toggleKeyboard}
            title="Toggle Greek keyboard"
            disabled={disabled}
          >
            Ω
          </button>
        )}
      </div>

      {/* Input method hints */}
      {showInputHints && showHints && (
        <div className="input-hints">
          <p className="hint-text">
            💡 Tip: Type Latin letters (a, b, g, d...) and they'll be converted to Greek (α, β, γ, δ...)
          </p>
          <p className="hint-text">
            Or use the Ω button to access the virtual Greek keyboard
          </p>
        </div>
      )}

      {/* Virtual Greek keyboard */}
      {showVirtualKeyboard && showKeyboard && (
        <div className="virtual-keyboard">
          <div className="keyboard-header">
            <span className="keyboard-title">Greek Keyboard</span>
            <button
              type="button"
              className="keyboard-close-btn"
              onClick={toggleKeyboard}
              title="Close keyboard"
            >
              ×
            </button>
          </div>
          <div className="keyboard-layout">
            {GREEK_KEYBOARD_LAYOUT.map((row, rowIndex) => (
              <div key={rowIndex} className="keyboard-row">
                {row.map((char) => (
                  <button
                    key={char}
                    type="button"
                    className="keyboard-key"
                    onClick={() => insertGreekChar(char)}
                    disabled={disabled}
                  >
                    {char}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation feedback */}
      {value && !isValid && (
        <div className="validation-message error">
          Invalid characters detected. Please use valid Greek Unicode characters.
        </div>
      )}

      {value && isValid && hasGreek && (
        <div className="validation-message success">
          ✓ Greek characters rendering correctly
        </div>
      )}
    </div>
  );
};

export default GreekInput;
